/* =============================================================================
   custom.variant-guard.js
   -----------------------------------------------------------------------------
   Corrige la sélection de variantes (couleur / grandeur) sur la fiche produit
   ET sur les vignettes de collection.

   POURQUOI UN FICHIER .js SÉPARÉ, ET PAS UN <script> DANS UN SNIPPET LIQUID :
   les vignettes de collection chargent le formulaire produit avec
   `element.innerHTML = html`. Or un navigateur n'exécute JAMAIS un <script>
   inséré par innerHTML (c'est la spécification HTML, pas un bug du thème).
   Un <script> inline ne s'appliquerait donc qu'à la fiche produit — c'est
   exactement la cause du bug « les tailles ne se mettent pas à jour » sur les
   pages collection.

   RÈGLES APPLIQUÉES — identiques au thème Horizon de Shopify :
     1. on ne décoche JAMAIS une option ;
     2. on ne masque JAMAIS une option pour cause de stock. Seul le métafield
        variant.custom.masquer masque, car c'est une décision volontaire d'ADM ;
     3. une option non achetable est marquée (classe swatches__disabled +
        aria-disabled="true") mais reste cliquable — on n'utilise jamais
        l'attribut HTML `disabled`, qui empêche de comprendre pourquoi et casse
        la navigation au clavier et les lecteurs d'écran ;
     4. la disponibilité d'une option est calculée HIÉRARCHIQUEMENT, de gauche
        à droite : chaque option n'est évaluée que contre celles qui la
        précèdent. C'est ce qui évite le mur d'options grisées et garde un
        parcours praticable ;
     5. si la combinaison cochée n'existe pas au catalogue, le champ `id` du
        formulaire est vidé et le bouton passe à « Non disponible ». Si elle
        existe mais est en rupture, l'id est conservé et le bouton dit
        « Épuisé ».

   La règle 4 est le garde-fou : elle rend impossible l'envoi au panier d'une
   variante différente de celle affichée au client. C'est elle qui corrige les
   factures avec la mauvaise grandeur.

   Ce fichier remplace deux <script> inline à supprimer :
     - snippets/product-form-options.liquid  (bloc « Script de desactivation… »)
     - snippets/product-form.liquid          (bloc « PATCH pour 3e option »)
   ========================================================================== */

(function () {
  'use strict';

  var MARK = 'swatches__disabled';     // classe déjà utilisée par le thème (opacité 30 %)
  var HIDE = 'variant-option-hidden';  // classe déjà utilisée par le thème (display:none)

  // Note : le masquage des valeurs entièrement indisponibles (ex. une couleur
  // sans aucune grandeur en stock) a été volontairement laissé de côté pour ce
  // correctif, afin de garder le périmètre minimal. À rediscuter séparément.

  function log(message, error) {
    if (window.console && console.warn) {
      console.warn('[variant-guard] ' + message, error || '');
    }
  }

  /* ---------------------------------------------------------------------------
     Lecture de l'état du formulaire
     ------------------------------------------------------------------------ */

  // Positions d'options réellement présentes dans CE formulaire : [1, 2, 3...]
  function positions(pf) {
    var seen = {};
    pf.allSwatches.forEach(function (input) {
      var p = Number(input.dataset.optionPosition);
      if (p) { seen[p] = true; }
    });
    return Object.keys(seen).map(Number).sort();
  }

  // Valeurs cochées, par position : { 1: 'Gris Clair', 2: '3TG' }
  function checkedValues(pf) {
    var selection = {};
    pf.allSwatches.forEach(function (input) {
      if (input.checked) {
        selection[Number(input.dataset.optionPosition)] = input.value;
      }
    });
    return selection;
  }

  // Ids des variantes masquées volontairement (métafield variant.custom.masquer)
  function hiddenIds(pf) {
    if (pf._vgHidden) { return pf._vgHidden; }

    var map = {};
    var el = pf.querySelector('[id^="ProductMetafields-"]');

    if (el) {
      try {
        JSON.parse(el.textContent).forEach(function (row) {
          var value = row.masquer;
          // Tolère false, "false" ou { value: false } : selon la version de
          // Liquid, `metafields.custom.masquer | json` peut sérialiser l'objet
          // métafield complet au lieu du simple booléen.
          if (value && typeof value === 'object') { value = value.value; }
          if (value === true || value === 'true') { map[row.id] = true; }
        });
      } catch (e) {
        log('métafield "masquer" illisible — aucune variante ne sera masquée', e);
      }
    }

    pf._vgHidden = map;
    return map;
  }

  // Variantes exploitables = celles du produit, moins les masquées
  function variants(pf) {
    if (!pf.product || !Array.isArray(pf.product.variants)) { return []; }
    var hidden = hiddenIds(pf);
    return pf.product.variants.filter(function (v) { return !hidden[v.id]; });
  }

  function optionOf(variant, position) {
    return variant['option' + position];
  }

  /* ---------------------------------------------------------------------------
     Cœur du correctif
     ------------------------------------------------------------------------ */

  // La variante correspondant EXACTEMENT à ce qui est coché.
  // Retourne null dès qu'une seule option n'est pas cochée : aucun repli
  // approximatif. C'est précisément ce repli — la fonction du thème retombait
  // sur la première variante de la couleur — qui envoyait la mauvaise grandeur.
  function guardedVariant(pf) {
    var pos = positions(pf);
    var selection = checkedValues(pf);

    if (!pos.length) {
      var seule = variants(pf);
      return seule.length === 1 ? seule[0] : null;
    }

    for (var i = 0; i < pos.length; i++) {
      if (selection[pos[i]] === undefined) { return null; }  // sélection incomplète
    }

    var matches = variants(pf).filter(function (v) {
      return pos.every(function (p) { return optionOf(v, p) === selection[p]; });
    });

    return matches[0] || null;
  }

  // Marque chaque pastille : achetable, non achetable, ou masquée.
  // Traitée groupe d'options par groupe d'options, parce que la décision de
  // masquer doit tenir compte de ce qui restera visible dans le groupe.
  function markSwatches(pf) {
    var list = variants(pf);
    if (!list.length) { return; }

    var pos = positions(pf);
    var selection = checkedValues(pf);

    pos.forEach(function (p) {
      var inputs = pf.allSwatches.filter(function (input) {
        return Number(input.dataset.optionPosition) === p;
      });

      /* --- passe 1 : que garde-t-on à l'affichage dans ce groupe ? ---------
         On ne masque QUE les valeurs dont toutes les variantes sont masquées
         par le métafield custom.masquer. Jamais pour une raison de stock, et
         jamais la valeur sélectionnée : c'est ce masquage qui faisait
         disparaître la ligne « LONGUEUR » entière du Paja S4.               */
      var visible = inputs.filter(function (input) {
        if (input.checked) { return true; }
        return list.some(function (v) {
          if (optionOf(v, p) !== input.value) { return false; }
          return pos.every(function (other) {
            if (other >= p || selection[other] === undefined) { return true; }
            return optionOf(v, other) === selection[other];
          });
        });
      });

      // Garde-fou : ne jamais vider complètement un groupe d'options.
      if (!visible.length) { visible = inputs; }

      /* --- passe 2 : marquage achetable / non achetable -------------------- */
      inputs.forEach(function (input) {
        var label = input.closest('label');
        var shown = visible.indexOf(input) !== -1;

        if (label) { label.classList.toggle(HIDE, !shown); }
        if (!shown) { return; }

        /* Existe-t-il une variante ACHETABLE avec cette valeur ?
           ---------------------------------------------------------------
           ÉVALUATION HIÉRARCHIQUE, de gauche à droite, comme Horizon et Dawn :
           on ne compare qu'avec les options qui PRÉCÈDENT celle-ci (other < p),
           jamais avec celles qui la suivent.

           Pourquoi c'est important : sur le Paja S4, en Gris/Bleu avec la
           taille 30, aucune longueur n'a de variante. En comparant avec TOUTES
           les options cochées, les trois longueurs se grisaient d'un coup et le
           client se retrouvait dans un cul-de-sac, sans comprendre que
           l'option bloquante était la taille. En ne comparant qu'avec la
           couleur, Régulier et Court restent cliquables et le parcours reste
           praticable.

           Conséquence à connaître : l'ordre des options dans l'admin Shopify
           devient signifiant. Couleur → Longueur → Taille est le bon ordre.  */
        var buyable = list.some(function (v) {
          if (!v.available) { return false; }
          if (optionOf(v, p) !== input.value) { return false; }
          return pos.every(function (other) {
            if (other >= p) { return true; }              // options suivantes : ignorées
            if (selection[other] === undefined) { return true; }
            return optionOf(v, other) === selection[other];
          });
        });

        input.classList.toggle(MARK, !buyable);
        input.setAttribute('aria-disabled', buyable ? 'false' : 'true');
        if (label) { label.classList.toggle(MARK, !buyable); }

        // Volontairement : on ne met JAMAIS input.disabled = true.
        // Et on retire un disabled éventuellement posé par un autre script :
        // une option doit toujours rester cliquable, sinon le client est bloqué
        // sans explication (cf. la ligne LONGUEUR vide du Paja S4).
        if (input.disabled) { input.disabled = false; }
      });
    });
  }

  /* Le garde-fou : dernier mot sur le champ envoyé au panier.
     Trois états, comme Horizon :
       - variante trouvée et disponible → id posé, bouton « Ajouter au panier » ;
       - variante trouvée mais en rupture → id CONSERVÉ, bouton « Épuisé ».
         On garde l'id volontairement : le bouton est désactivé de toute façon,
         et une app de réappro ou de notification « prévenez-moi » a besoin de
         savoir de quelle variante il s'agit ;
       - aucune variante (la combinaison n'existe pas) → id VIDÉ, bouton
         « Non disponible ».
     Les libellés viennent du thème (products.add_to_cart / sold_out /
     unavailable) via updateProductButton, qui gère déjà les trois cas.       */
  function applyGuard(pf, variant) {
    if (!variant) {
      // Vider le <select name="id"> : FormData n'enverra alors aucun id,
      // donc /cart/add.js ne peut pas ajouter la mauvaise variante.
      if (pf.selectId) { pf.selectId.value = ''; }
      if (pf.submitButton) { pf.submitButton.disabled = true; }
      // Le thème cache le prix quand la variante est nulle. On le laisse
      // visible, comme Horizon, pour ne pas donner l'impression d'une page cassée.
      if (pf.priceWrapperElement) { pf.priceWrapperElement.classList.remove('hide'); }
      return;
    }

    if (pf.selectId) { pf.selectId.value = String(variant.id); }
    if (!variant.available && pf.submitButton) { pf.submitButton.disabled = true; }
  }

  /* ---------------------------------------------------------------------------
     Application du patch sur le composant <product-form>
     ------------------------------------------------------------------------ */

  function patch() {
    var Ctor = window.customElements.get('product-form');
    if (!Ctor || !Ctor.prototype || Ctor.prototype._variantGuard) { return; }
    Ctor.prototype._variantGuard = true;

    var origUpdateProductForm = Ctor.prototype.updateProductForm;
    var origBrowserHistory    = Ctor.prototype.updateBrowserHistory;
    var origCardImage         = Ctor.prototype.updateCardImage;

    // Point d'entrée unique : toutes les mises à jour du formulaire passent ici
    // (initialisation, changement d'option, sélection par SKU).
    Ctor.prototype.updateProductForm = function (variantFromCaller) {
      var variant = this.product ? guardedVariant(this) : variantFromCaller;

      try {
        origUpdateProductForm.call(this, variant);
      } catch (e) {
        log('erreur dans la chaîne de mise à jour du thème', e);
      }

      // Toujours exécuté, même si la chaîne du thème a échoué au-dessus :
      // le garde-fou ne doit jamais pouvoir être contourné.
      try {
        markSwatches(this);
        applyGuard(this, variant);
      } catch (e) {
        log('erreur du garde-fou', e);
      }
    };

    // Remplace le calcul de disponibilité du thème, qui lisait la valeur de
    // l'option 2 sur l'input de l'option 1 et ne gérait pas la 3e option.
    Ctor.prototype.updateOutOfStockOptions = function () {
      try { markSwatches(this); } catch (e) { log('marquage des pastilles', e); }
    };

    // Le thème réécrit l'URL avec ?variant=... même depuis une vignette de
    // collection, ce qui pollue l'URL de la page collection au simple survol.
    Ctor.prototype.updateBrowserHistory = function (variant) {
      if (this.closest('product-card')) { return; }
      return origBrowserHistory.call(this, variant);
    };

    // updateCardImage du thème plante quand la variante est nulle
    // (il lit variant.featured_media sans vérifier que variant existe).
    Ctor.prototype.updateCardImage = function (variant) {
      if (!variant || !variant.featured_media) { return; }
      return origCardImage.call(this, variant);
    };

    /* Dernière ligne de défense, au moment de l'envoi.
       ---------------------------------------------------------------------
       Le bouton désactivé suffit dans 99 % des cas, mais un formulaire peut
       aussi être envoyé par la touche Entrée depuis le champ quantité. Cet
       écouteur est posé sur `document` en phase de CAPTURE : il s'exécute donc
       avant tous les écouteurs du thème, y compris celui qui sérialise le
       formulaire. On bloque uniquement le cas dangereux — la combinaison
       n'existe pas au catalogue — jamais une simple rupture de stock.        */
    document.addEventListener('submit', function (event) {
      var form = event.target;
      if (!form || form.tagName !== 'FORM') { return; }

      var host = form.closest('product-form');
      if (!host || !host.product) { return; }

      try {
        if (guardedVariant(host) === null) {
          event.preventDefault();
          event.stopImmediatePropagation();
          log('envoi bloqué : la combinaison sélectionnée n\'existe pas au catalogue');
        }
      } catch (e) {
        log('vérification à l\'envoi', e);
      }
    }, true);

    // Recalcule l'état des formulaires déjà présents dans la page.
    document.querySelectorAll('product-form').forEach(function (el) {
      try {
        if (el.product && typeof el.updateProductForm === 'function') {
          el.updateProductForm();
        }
      } catch (e) {
        log('recalcul initial', e);
      }
    });
  }

  if (window.customElements) {
    if (window.customElements.get('product-form')) {
      patch();
    } else {
      window.customElements.whenDefined('product-form').then(patch);
    }
  }

  // L'éditeur de thème recharge les sections sans recharger la page.
  document.addEventListener('shopify:section:load', patch);
})();
